// useFieldTemplateForm.ts
import { SetStateAction, useCallback, useEffect, useState } from 'react'
import { sortStep, maxFieldNameLength, maxFieldDescriptionLength } from '../../utils/constants'
import { CategoryFieldTemplateInterface } from '../../types/interfaces'
import { CategoryFieldType } from '../../types/enums'
import { v4 } from 'uuid'

const itemPattern: Omit<CategoryFieldTemplateInterface, 'id' | 'createdAt' | 'updatedAt'> = {
  name: '',
  description: '',
  type: CategoryFieldType.NONE,
  isActive: true,
  isVisibleForClient: true,
  isFillByClient: true,
  required: false,
  sortOrder: sortStep,
}

export const useFieldTemplateForm = <T extends { fields: CategoryFieldTemplateInterface[] }>(
  topData: T,
  setTopData: React.Dispatch<React.SetStateAction<T>>,
  setDisabled: React.Dispatch<SetStateAction<boolean>>,
) => {
  const [data, setData] = useState<CategoryFieldTemplateInterface[]>([...topData.fields])

  const addItem = useCallback((el: CategoryFieldTemplateInterface) => {
    setData((prev) =>
      prev
        .toSpliced(prev.indexOf(el) + 1, 0, {
          ...itemPattern,
          id: v4(),
        })
        .map((item, idx) => ({ ...item, sortOrder: (idx + 1) * sortStep })),
    )
  }, [])

  const removeItem = useCallback((el: CategoryFieldTemplateInterface) => {
    setData((prev) =>
      prev
        .toSpliced(prev.indexOf(el), 1)
        .map((item, idx) => ({ ...item, sortOrder: (idx + 1) * sortStep })),
    )
  }, [])

  const itemUp = useCallback(
    (el: CategoryFieldTemplateInterface) => {
      const i = data.indexOf(el)
      if (i <= 0) return
      const arr = [...data]
      ;[arr[i], arr[i - 1]] = [arr[i - 1], arr[i]]
      setData(arr.map((item, idx) => ({ ...item, sortOrder: (idx + 1) * sortStep })))
    },
    [data],
  )

  const itemDown = useCallback(
    (el: CategoryFieldTemplateInterface) => {
      const i = data.indexOf(el)
      if (i < 0 || i >= data.length - 1) return
      const arr = [...data]
      ;[arr[i], arr[i + 1]] = [arr[i + 1], arr[i]]
      setData(arr.map((item, idx) => ({ ...item, sortOrder: (idx + 1) * sortStep })))
    },
    [data],
  )

  const handleFieldChange = useCallback(
    (id: string, updates: Partial<CategoryFieldTemplateInterface>) => {
      setData((prev) =>
        prev.map((field) => (String(field.id) === String(id) ? { ...field, ...updates } : field)),
      )
    },
    [],
  )

  // useEffect(() => {
  //   setTopData((prev) => ({
  //     ...prev,
  //     fields: data.map(({ id, ...keepFields }) => ({ ...keepFields })),
  //   }))
  // }, [data, setTopData])

  useEffect(() => {
  setTopData((prev) => ({
    ...prev,
    fields: data.map(({ id, ...rest }) => ({
      ...rest,
      // id передаём только если это число (существующее с бэка)
      ...(typeof id === 'number' ? { id } : {}),
    })),
  }))
}, [data, setTopData])

  useEffect(() => {
    setDisabled(
      Boolean(data.find((el) => !el.name)) ||
        Boolean(data.find((el) => el.name.length > maxFieldNameLength)) ||
        Boolean(data.find((el) => el.type === CategoryFieldType.NONE)) ||
        Boolean(
          data.find((el) => el.description && el.description.length > maxFieldDescriptionLength),
        ),
    )
  }, [data, setDisabled])

  return {
    data,
    addItem,
    removeItem,
    itemUp,
    itemDown,
    handleFieldChange,
    itemPattern,
  }
}
